package com.ragnorak.clanybackoffice.ui.screens

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.zacsweers.metro.Inject
import com.clany.backoffice.domain.usecase.GetAccountsUseCase
import com.clany.backoffice.domain.usecase.DeleteAccountUseCase
import com.clany.backoffice.model.AccountResponse
import kotlinx.coroutines.launch

data class AccountsUiState(
    val isLoading: Boolean = false,
    val accounts: List<AccountResponse> = emptyList(),
    val error: String? = null,
    val page: Int = 1,
    val hasMore: Boolean = true,
    val selectedAccount: AccountResponse? = null,
    val editingAccount: AccountResponse? = null,
    val deletingAccountId: String? = null,
    val searchQuery: String = ""
)

class AccountsViewModel @Inject constructor(
    private val getAccountsUseCase: GetAccountsUseCase,
    private val updateAccountUseCase: com.clany.backoffice.domain.usecase.UpdateAccountUseCase,
    private val deleteAccountUseCase: DeleteAccountUseCase
) : ViewModel() {

    var uiState by mutableStateOf(AccountsUiState())
        private set

    init {
        loadAccounts()
    }

    fun loadAccounts(page: Int = 1) {
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            getAccountsUseCase(page = page, pageSize = 20)
                .onSuccess { accounts ->
                    val filtered = if (uiState.searchQuery.isNotBlank()) {
                        accounts.filter { account ->
                            account.name.lowercase().contains(uiState.searchQuery.lowercase())
                        }
                    } else accounts

                    uiState = uiState.copy(
                        isLoading = false,
                        accounts = if (page == 1) filtered else uiState.accounts + filtered,
                        page = page,
                        hasMore = accounts.isNotEmpty()
                    )
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun onSearchQueryChanged(query: String) {
        uiState = uiState.copy(searchQuery = query)
        loadAccounts(page = 1)
    }

    fun onSelectAccount(account: AccountResponse) {
        uiState = uiState.copy(selectedAccount = account)
    }

    fun dismissDetail() {
        uiState = uiState.copy(selectedAccount = null)
    }

    fun onEditAccount(account: AccountResponse) {
        uiState = uiState.copy(editingAccount = account)
    }

    fun dismissEdit() {
        uiState = uiState.copy(editingAccount = null)
    }

    fun onDeleteAccount(accountId: String) {
        uiState = uiState.copy(deletingAccountId = accountId)
    }

    fun dismissDelete() {
        uiState = uiState.copy(deletingAccountId = null)
    }

    fun confirmDelete() {
        val accountId = uiState.deletingAccountId ?: return
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true)
            deleteAccountUseCase(accountId)
                .onSuccess {
                    val updatedList = uiState.accounts.filter { it.id != accountId }
                    uiState = uiState.copy(isLoading = false, accounts = updatedList, deletingAccountId = null)
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }

    fun updateAccount(id: String, name: String) {
        viewModelScope.launch {
            uiState = uiState.copy(isLoading = true)
            updateAccountUseCase(com.clany.backoffice.model.UpdateAccountRequest(id, name))
                .onSuccess { updatedAccount ->
                    val updatedList = uiState.accounts.map { if (it.id == updatedAccount.id) updatedAccount else it }
                    uiState = uiState.copy(
                        isLoading = false, 
                        accounts = updatedList, 
                        selectedAccount = null,
                        editingAccount = null
                    )
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false, error = it.message)
                }
        }
    }
}
